import { useCallback, useEffect, useRef, useState } from "react";
import { FiZoomIn, FiZoomOut, FiMaximize2 } from "react-icons/fi";

import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from "./constants";

/**
 * Zoom/pan viewer for a still image. Props-driven and self-contained so both
 * the asset preview modal and the assets-list overlay can mount it.
 *
 * @input {asset: {_id: 'a1', url: 'https://…/still.png', name: 'Still'}}
 */
export default function ImagePreview({ asset }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef(null);
  // Keep refs to the latest scale / pinch baseline so the non-passive wheel
  // and gesture listeners (attached via addEventListener in useEffect) can
  // read current state without being re-bound on every render.
  const scaleRef = useRef(scale);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  const gestureStartScaleRef = useRef(1);

  const clampScale = (s) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, s));

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reset zoom when asset changes
  useEffect(() => {
    reset();
  }, [asset?._id, asset?.url, reset]);

  // Zoom around a focal point (clientX/clientY in viewport coords). Keeps the
  // pixel under the cursor anchored as the scale changes.
  const zoomAt = useCallback((nextScale, focalClientX, focalClientY) => {
    const target = clampScale(nextScale);
    setScale((prevScale) => {
      const clamped = clampScale(target);
      if (clamped === prevScale) return prevScale;
      const container = containerRef.current;
      if (!container) return clamped;
      const rect = container.getBoundingClientRect();
      const cx =
        focalClientX != null ? focalClientX - rect.left - rect.width / 2 : 0;
      const cy =
        focalClientY != null ? focalClientY - rect.top - rect.height / 2 : 0;
      setOffset((prevOffset) => {
        const ratio = clamped / prevScale;
        const nx = cx - (cx - prevOffset.x) * ratio;
        const ny = cy - (cy - prevOffset.y) * ratio;
        if (clamped <= 1) return { x: 0, y: 0 };
        return { x: nx, y: ny };
      });
      return clamped;
    });
  }, []);

  // Wheel + Safari gesture listeners. We attach these manually (instead of
  // using React's onWheel) so we can opt out of the default passive mode
  // and call preventDefault — otherwise the browser still applies its
  // page-zoom gesture on top of ours.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      const isZoomGesture = e.ctrlKey || e.metaKey;
      if (isZoomGesture) {
        // Trackpad pinch (ctrlKey set by the browser) and Cmd/Ctrl+wheel both zoom.
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.01);
        zoomAt(scaleRef.current * factor, e.clientX, e.clientY);
        return;
      }
      if (scaleRef.current > 1) {
        e.preventDefault();
        setOffset((prev) => ({
          x: prev.x - e.deltaX,
          y: prev.y - e.deltaY,
        }));
      }
    };

    // Safari fires non-standard gesture* events for trackpad pinch. They
    // bypass the wheel path entirely and trigger page zoom unless we
    // preventDefault them. e.scale is multiplicative relative to gesturestart.
    const onGestureStart = (e) => {
      e.preventDefault();
      gestureStartScaleRef.current = scaleRef.current;
    };
    const onGestureChange = (e) => {
      e.preventDefault();
      const next = gestureStartScaleRef.current * (e.scale || 1);
      zoomAt(next, e.clientX, e.clientY);
    };
    const onGestureEnd = (e) => {
      e.preventDefault();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("gesturestart", onGestureStart, { passive: false });
    el.addEventListener("gesturechange", onGestureChange, { passive: false });
    el.addEventListener("gestureend", onGestureEnd, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGestureStart);
      el.removeEventListener("gesturechange", onGestureChange);
      el.removeEventListener("gestureend", onGestureEnd);
    };
  }, [zoomAt]);

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!dragState.current) return;
    const { startX, startY, originX, originY } = dragState.current;
    setOffset({
      x: originX + (e.clientX - startX),
      y: originY + (e.clientY - startY),
    });
  };

  const endDrag = () => {
    dragState.current = null;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-black overflow-hidden select-none touch-none overscroll-contain"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      style={{
        cursor:
          scale > 1 ? (dragState.current ? "grabbing" : "grab") : "default",
      }}>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: dragState.current ? "none" : "transform 120ms ease-out",
        }}>
        <img
          src={asset.url || asset.thumbnail_url}
          alt={asset.name}
          decoding="async"
          draggable={false}
          className="object-contain w-full h-full"
        />
      </div>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-md bg-black/60 backdrop-blur px-1 py-1 text-white text-xs">
        <button
          type="button"
          title="Zoom out"
          disabled={scale <= MIN_ZOOM}
          onClick={() => zoomAt(scale - ZOOM_STEP)}
          className="p-1 rounded hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent">
          <FiZoomOut className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Reset zoom"
          onClick={reset}
          className="px-2 py-1 rounded hover:bg-white/10 min-w-[3.25rem] text-center tabular-nums">
          {Math.round(scale * 100)}%
        </button>
        <button
          type="button"
          title="Zoom in"
          disabled={scale >= MAX_ZOOM}
          onClick={() => zoomAt(scale + ZOOM_STEP)}
          className="p-1 rounded hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent">
          <FiZoomIn className="w-4 h-4" />
        </button>
        <div className="w-px h-4 bg-white/20 mx-1" />
        <button
          type="button"
          title="Fit to screen"
          onClick={reset}
          className="p-1 rounded hover:bg-white/10">
          <FiMaximize2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
